/**
 * DeletableEdge — smoothstep edge with a hover "X" button at its midpoint.
 *
 * Clicking the X calls editorStore.deleteEdge(id), which clears the source
 * node's pointer field. Hover state is driven by CSS + pointer events on the
 * edge's interaction layer.
 */

import { useState } from "react";
import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  type EdgeProps,
} from "@xyflow/react";
import { X } from "lucide-react";
import { useEditorStore } from "../editorStore";

export function DeletableEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  markerEnd,
  style,
}: EdgeProps) {
  const deleteEdge = useEditorStore((s) => s.deleteEdge);
  const [hovered, setHovered] = useState(false);

  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    curvature: 0.35,
  });

  return (
    <>
      <BaseEdge id={id} path={edgePath} markerEnd={markerEnd} style={style} />
      {/* Wide invisible hit-area so hover + click land reliably */}
      <path
        d={edgePath}
        fill="none"
        stroke="transparent"
        strokeWidth={24}
        className="react-flow__edge-interaction"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{ pointerEvents: "stroke", cursor: "pointer" }}
      />
      <EdgeLabelRenderer>
        <div
          style={{
            position: "absolute",
            transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
            pointerEvents: "all",
          }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              deleteEdge(id);
            }}
            className={
              "flex items-center justify-center w-5 h-5 rounded-full " +
              "bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-700 " +
              "text-neutral-600 dark:text-neutral-300 shadow-sm " +
              "hover:bg-category-error hover:text-white hover:border-category-error " +
              "transition-opacity " +
              (hovered ? "opacity-100" : "opacity-0")
            }
            aria-label="Remove connection"
            title="Remove connection"
          >
            <X className="w-3 h-3" strokeWidth={2.5} />
          </button>
        </div>
      </EdgeLabelRenderer>
    </>
  );
}

export const edgeTypes = {
  deletable: DeletableEdge,
};
